import { memo, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { useStore } from "../store/globalState";
import { FirstSendWarning } from "../components/first-send/FirstSendWarning";
import { useFirstSendWarning } from "../hooks/useFirstSendWarning";
import { shouldPromptPhone } from "../utils/phoneGate";
import { ensureSmsConsent } from "../components/notifications/smsConsent";
import { useSessionStore } from "../store/useSessionStore";
import { useLocation, useNavigate } from "react-router-dom";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { Trans, useTranslation } from "react-i18next";
import { BodyCopy } from "../components/BodyCopy";
import {
  BankIcon,
  Button,
  CashSpecialIcon,
  ExchangeIcon,
  Layout,
  SupportIcon,
  Title,
  WalletIcon,
  WarningIcon,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import { RequireActiveClerkSession } from "../hooks/useRequireActiveClerkSession";
import { useClaimLink } from "../hooks/useClaimLink";
import { RequestStatus } from "../utils/commons";
import { FlagCircle } from "../components/FlagCircle";
import { QuoteInfo } from "../components/QuoteInfo";
import { amountFormatter, formatLabeledAmount, formatMoney } from "../utils";
import { isInitialTransferPaintReady } from "../utils/initialTransferPaint";
import { PayoutInfo } from "../components/PayoutInfo";
import { FooterDetailSummary } from "../components/FooterDetailSummary";
import { Popup } from "../components/Popup";
import {
  SelectFundingMethodSlot,
  type FlatFundingPick,
} from "../components/SelectFundingMethodSlot";
import { SelectDestinationCurrencySlot } from "../components/SelectDestinationCurrencySlot";
import { RouteUnavailablePage } from "./RouteUnavailablePage";
import { AmountOutOfRangeInfo } from "../components/AmountOutOfRangeInfo";
import { paymentType } from "../interfaces/payment";
import masterCardLogo from "../assets/master_card_icon.svg";
import visaLogo from "../assets/visa_color_icon.svg";
import { useTransferPricing } from "../hooks/useTransferPricing";
import { COUNTRY_CURRENCY } from "../hooks/useExchangeRate";
import { useFundingMethodLimits } from "../hooks/useFundingMethodLimits";
import { usePaymentMethods } from "../hooks/usePaymentMethods";
import { usePayerName } from "../hooks/usePayerName";
import {
  bankAccountRowLabel,
  formatBankAccountLabel,
} from "../utils/bankAccountLabel";
import {
  bestCustomerFeeForFundingMethod,
  FUNDING_METHODS,
  isFundingMethod,
  type FundingMethod,
} from "../utils/catalog";
import {
  compareSendToBound,
  corridorsForFundingMethod,
  skusForCorridors,
} from "../utils/fundingLimits";
import { checkCipReadiness, isDeadLinkError } from "../services/api";
import { useRequestPayableGuard } from "../hooks/useRequestPayableGuard";
import { prepareFundingRail } from "../utils/prepare-funding-rail";
import { CvvPrepareGate } from "../components/CvvPrepareGate";
import { UnavailableFundingCardSheet } from "../components/UnavailableFundingCardSheet";
import type { ICardPaymentMethod } from "../interfaces/payment-method";
import {
  reasonFromCardPayCode,
  type UnavailableCardReason,
} from "../utils/card-funding-availability";
import {
  catalogFundingMethodOf,
  type AddCardClassNotice,
} from "../utils/resolve-add-card-funding-class";
import { Toast } from "../components/Toast";
import { persistedCardIsMissing } from "../utils/persistFundingSelection";
import {
  isFundingCardBlocked,
  resolveFundingCardFromState,
} from "../utils/resolve-funding-card";
import type {
  FundingInstrument,
  FundingInstrumentSaved,
} from "../interfaces/funding";
import { isFundingInstrumentReady } from "../interfaces/funding";
import { getEvents, safeCapture } from "../analytics";
import { quoteMatchesSelection } from "../utils/quoteMatchesSelection";
import { useQuoteLoadingTimedOut } from "../hooks/useQuoteLoadingTimedOut";
import { useSupportSheet } from "../hooks/useSupportSheet";
import { isAmountEditable, resolveAmountAnchor } from "../utils/amountAnchor";
import {
  isDestinationCurrencyEditable,
  isPayoutEditable,
  shouldPreparePayoutRail,
} from "../utils/payoutAnchor";
import { claimDefaultPayoutSelection } from "../utils/claimPayoutCompose";
import { useComposeStore } from "../store/useComposeStore";
import { FundRequestPayoutPopups } from "../components/FundRequestPayoutPopups";
import { preparePayoutRail } from "../utils/prepare-payout-rail";
import { isPayoutCardValidationStale } from "../utils/pre-validate-payout-card";
import { PayoutMethodType } from "../interfaces/payout";
import { useCatalogCountries } from "../hooks/useCatalog";
import {
  corridorCurrencyCodesByDestination,
  expandCountryCurrencyOptions,
  mergeCatalogWithCorridorCurrencies,
} from "../utils/destinationOptions";
import { countryDisplayName } from "../utils/country-display";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { Navbar } from "../components/Navbar";


type PaymentPopup = "funding" | "amountLimit" | "payout" | null;

/**
 * `primaryMesh` is usually a gradient string, which is invalid for `color` and
 * `border-color`. The outlined icon in the rate pill needs a solid value, so it
 * falls back to the gradient's first stop.
 */
const solidStop = (color: string) =>
  color.match(/#[0-9a-f]{3,8}|rgba?\([^)]*\)/i)?.[0] ?? color;

const sheetCardFromNotice = (
  card: AddCardClassNotice["card"]
): ICardPaymentMethod => ({
  paymentMethodId: card.paymentMethodId,
  brand: card.brand,
  last4: card.last4,
  expiryMonth: "",
  expiryYear: "",
  enabled: true,
  isDefault: false,
  fundingMethod: card.fundingMethod,
  productClass: card.productClass,
});

export const TransferPage = memo(() => {
  // Deep link / new tab / refresh: claim before the quote shell paints.
  // Same-tab "Pay" already has a LOADED session and this is a no-op.
  useClaimLink();
  return (
    <RequireActiveClerkSession resume={{ kind: "transfer" }} requireOccupant>
      <TransferPageContent />
    </RequireActiveClerkSession>
  );
});

const TransferPageContent = memo(() => {
  useRequestPayableGuard();
  const { t, i18n } = useTranslation();
  const {
    theme,
    amountState,
    paymentState,
    quoteState,
    setQuoteState,
    setAmountState,
    setPaymentState,
    fundingInstrument,
    setFundingInstrument,
    setPreparedFundingPaymentMethodId,
    pendingFundingMethod,
    setPendingFundingMethod,
    setPreparedPayoutPaymentMethodId,
  } = useStore();
  const { session } = useSessionStore();
  const colorConfig = theme.whiteView;
  const {
    backgroundBtn,
    textColorBtn,
    title,
    bodyCopy,
    dropzoneColors,
    bannerHighlightBackground,
    bannerHighlightColor,
    bannerHighlightIconColors,
  } = colorConfig as typeof colorConfig & {
    bannerHighlightBackground?: string;
    bannerHighlightColor?: string;
    bannerHighlightIconColors?: string[];
  };
  const navigate = useNavigate();
  const location = useLocation();
  const { redirectTo } = useRouteWithToken();
  const sessionReady =
    session.status === RequestStatus.LOADED && Boolean(session.data);
  const cardClassNotice = (
    location.state as { cardClassNotice?: AddCardClassNotice } | null
  )?.cardClassNotice;
  const cardClassNoticeHandledRef = useRef(false);

  useEffect(() => {
    if (session.status === RequestStatus.ERROR && session.error) {
      redirectTo(APP_ROUTES.ERROR_PAGE, {
        replace: true,
        state: session.error,
      });
    }
  }, [redirectTo, session.error, session.status]);

  useEffect(() => {
    if (!sessionReady) return;
    safeCapture(getEvents().SCREEN_VIEW("route_amount"));
    safeCapture(getEvents().SCREEN_VIEW("summary"));
  }, [sessionReady]);

  const [popup, setPopup] = useState<PaymentPopup>(null);
  const [currencyPopup, setCurrencyPopup] = useState<
    "source" | "destination" | null
  >(null);
  const [showExcratePopup, setShowExcratePopup] = useState(false);
  const [showRouteUnavailable, setShowRouteUnavailable] = useState(false);
  const [shake, setShake] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [prepareError, setPrepareError] = useState<string | null>(null);
  const [cvvGate, setCvvGate] = useState<{
    last4: string;
    brand?: string;
    funding: FundingInstrumentSaved;
    error: string | null;
  } | null>(null);
  const [blockedFundingCard, setBlockedFundingCard] =
    useState<ICardPaymentMethod | null>(null);
  const [blockedRailCards, setBlockedRailCards] = useState<
    ReadonlyMap<string, UnavailableCardReason>
  >(() => new Map());
  const [classChangedToast, setClassChangedToast] = useState<
    "DEBIT_CARD" | "CREDIT_CARD" | null
  >(null);

  useEffect(() => {
    const cardId =
      paymentState.payment?.type === paymentType.CARD
        ? paymentState.payment.cardId
        : null;
    if (!cardId || !blockedRailCards.has(cardId)) return;
    setFundingInstrument(null);
    setPaymentState({ payment: null });
  }, [
    blockedRailCards,
    paymentState.payment,
    setFundingInstrument,
    setPaymentState,
  ]);

  const config = session.data?.config;
  const amountEditable = isAmountEditable(config);
  const payoutEditable = isPayoutEditable(config);
  const destinationCurrencyEditable =
    isDestinationCurrencyEditable(config);
  const setDestinationCurrency = useComposeStore((s) => s.setDestinationCurrency);
  const setSourceCurrency = useComposeStore((s) => s.setSourceCurrency);
  const setComposeSelection = useComposeStore((s) => s.setSelection);
  const [, setPayoutRevision] = useState(0);

  const prepareErrorMessage = useCallback(
    (code: string | undefined): string => {
      switch (code) {
        case "PROVISION_FAILED":
          return t("transfer.prepare.errorProvision");
        case "CVV_REQUIRED":
          return t("transfer.prepare.errorCvv");
        case "REGISTER_FAILED":
          return t("transfer.prepare.errorRegister");
        case "READINESS_FAILED":
          return t("transfer.prepare.errorReadiness");
        case "VGS_CONFIG_UNAVAILABLE":
          return t("vgs.configUnavailable");
        case "CARD_PUSH_STALE":
          return t("payout.card.corridorChanged");
        default:
          return t("transfer.prepare.errorGeneric");
      }
    },
    [t]
  );

  // Pricing: runs a debounced pre-quote on amount change and a binding quote
  // once a payment method (SKU) is selected.
  const {
    amountAnchor,
    amount: pricingAmount,
    sourceCountry,
    destinationCountry,
    sourceCurrency,
    destinationCurrency,
    availableFundingMethods,
    preQuoteError,
    options,
    selectedOption,
    bestOption,
    capabilityKey,
    quote,
    quoteLoading,
    quoteError,
    refetchQuote,
    preQuoteLoading,
    routeUnavailable,
    payerCode,
    payoutMethod,
    composeSelection,
    allPayoutMethodsForPicker,
    payoutDiscoveryOptions,
    payoutDiscoveryLoading,
    economics,
    facets,
  } = useTransferPricing({ discoverPayouts: popup === "payout" });

  // Destination-card dry-run is corridor-scoped: clear it when the route changes.
  useEffect(() => {
    const selection = useComposeStore.getState().selection;
    if (
      !selection ||
      selection.method !== PayoutMethodType.PUSH_TO_CARD ||
      selection.origin !== "user"
    ) {
      return;
    }
    const payout = selection.payout;
    if (!payout.cvsValidationId && !payout.validatedFor) return;
    if (
      !isPayoutCardValidationStale(
        payout.validatedFor,
        sourceCountry,
        destinationCountry,
      )
    ) {
      return;
    }
    setComposeSelection({
      ...selection,
      payout: {
        ...payout,
        cvsValidationId: undefined,
        numberToken: undefined,
        validatedFor: undefined,
      },
    });
  }, [sourceCountry, destinationCountry, setComposeSelection]);

  const { data: catalogCountries } = useCatalogCountries(
    { originCountryCode: sourceCountry },
    destinationCurrencyEditable,
  );
  const destinationCurrencyOptions = useMemo(() => {
    if (!destinationCurrencyEditable) return [];
    const corridors = config?.corridors ?? [];
    const merged = mergeCatalogWithCorridorCurrencies(
      catalogCountries ?? [],
      [destinationCountry],
      corridorCurrencyCodesByDestination(corridors),
      (code) => countryDisplayName(code, i18n.language),
    );
    return expandCountryCurrencyOptions(merged).filter(
      (option) =>
        option.countryCode.toUpperCase() ===
        destinationCountry.toUpperCase(),
    );
  }, [
    catalogCountries,
    config?.corridors,
    destinationCountry,
    destinationCurrencyEditable,
    i18n.language,
  ]);

  const sourceCurrencyOptions = useMemo(() => {
    if (!destinationCurrencyEditable) return [];
    const codes = new Set<string>();
    if (config?.sourceCurrency) codes.add(config.sourceCurrency.toUpperCase());
    const local = COUNTRY_CURRENCY[sourceCountry];
    if (local) codes.add(local);
    for (const corridor of config?.corridors ?? []) {
      if (
        corridor.originCountryCode.toUpperCase() ===
        sourceCountry.toUpperCase()
      ) {
        for (const code of corridor.sourceCurrencyCodes ?? []) {
          codes.add(code.toUpperCase());
        }
      }
    }
    return [...codes].map((currencyCode) => ({
      countryCode: sourceCountry,
      countryName: countryDisplayName(sourceCountry, i18n.language),
      currencyCode,
      supportedByCorridors: [],
      effectiveStatus: "AVAILABLE" as const,
    }));
  }, [
    config?.corridors,
    config?.sourceCurrency,
    destinationCurrencyEditable,
    i18n.language,
    sourceCountry,
  ]);

  const handleDestinationCurrencySelect = useCallback(
    (currencyCode: string) => {
      if (!destinationCurrencyEditable) return;
      setDestinationCurrency(currencyCode);
      setQuoteState(null);

      // Currency changes alter payout offerability. Re-seed the requester
      // default and let pricing prove whether that route remains available.
      if (payoutEditable) {
        const defaultSelection = claimDefaultPayoutSelection(
          session.data?.userData,
        );
        if (defaultSelection) {
          setComposeSelection(defaultSelection);
          setPayoutRevision((revision) => revision + 1);
        }
      }
    },
    [
      destinationCurrencyEditable,
      payoutEditable,
      session.data?.userData,
      setComposeSelection,
      setDestinationCurrency,
      setPayoutRevision,
      setQuoteState,
    ],
  );

  const handleSourceCurrencySelect = useCallback(
    (currencyCode: string) => {
      if (!destinationCurrencyEditable) return;
      setSourceCurrency(currencyCode);
      setQuoteState(null);
      if (payoutEditable) {
        const defaultSelection = claimDefaultPayoutSelection(
          session.data?.userData,
        );
        if (defaultSelection) {
          setComposeSelection(defaultSelection);
          setPayoutRevision((revision) => revision + 1);
        }
      }
    },
    [
      destinationCurrencyEditable,
      payoutEditable,
      session.data?.userData,
      setComposeSelection,
      setPayoutRevision,
      setQuoteState,
      setSourceCurrency,
    ],
  );

  // The claim pins a payer code (e.g. `GT_BANCO_GYT_CASH`); the catalog owns
  // its human-readable name, so the summary never shows the raw code.
  const payerName = usePayerName(payerCode, destinationCountry, payoutMethod);
  const selectedBankCode =
    payoutEditable &&
    composeSelection?.origin === "user" &&
    composeSelection.method === PayoutMethodType.BANK_ACCOUNT
      ? composeSelection.payout.bankId
      : undefined;
  const selectedBankName = usePayerName(
    selectedBankCode,
    destinationCountry,
    PayoutMethodType.BANK_ACCOUNT,
  );

  const queriesForMethod = useCallback(
    (method: FundingMethod) => {
      const corridors = corridorsForFundingMethod(method, {
        options,
        facets,
      });
      return skusForCorridors(method, corridors, {
        payoutMethod,
        payerCode,
        customerFee: bestCustomerFeeForFundingMethod(method, facets, options),
      });
    },
    [facets, options, payerCode, payoutMethod]
  );
  const { details: fundingDetails, loading: fundingLimitsLoading } =
    useFundingMethodLimits(
      availableFundingMethods,
      queriesForMethod,
      sourceCountry,
      destinationCountry,
      sourceCurrency
    );

  const sendForBound =
    amountAnchor === "receive"
      ? (quote?.sendAmount ??
        selectedOption?.sendAmount ??
        economics?.sendAmount ??
        null)
      : pricingAmount;
  const selectedFundingDetails = isFundingMethod(pendingFundingMethod)
    ? fundingDetails[pendingFundingMethod]
    : undefined;
  const amountBoundVerdict = compareSendToBound(
    sendForBound,
    selectedFundingDetails
  );
  const amountOutOfRange =
    amountBoundVerdict === "below_min" || amountBoundVerdict === "above_max";

  const sourceAmountPrefix = sourceCurrency === "USD" ? "$" : "";
  const destinationAmountPrefix = destinationCurrency === "USD" ? "$" : "";

  // Warm the payment-methods cache on mount so the "select payment method"
  // popup opens with the list already populated (no fetch-on-open / pop-in).
  const {
    cards: registeredCards,
    bankAccounts,
    loading: paymentMethodsLoading,
    loaded: paymentMethodsLoaded,
    refresh: refreshPaymentMethods,
  } = usePaymentMethods(session.data?.linkId);
  const fundingPickerLoading =
    paymentMethodsLoading ||
    preQuoteLoading ||
    (availableFundingMethods.length === 0 &&
      !routeUnavailable &&
      !preQuoteError);

  const resolveFundingInstrument = useCallback((): FundingInstrument | null => {
    if (fundingInstrument && isFundingInstrumentReady(fundingInstrument)) {
      return fundingInstrument;
    }
    if (
      paymentState.payment?.type === paymentType.CARD &&
      paymentState.payment.cardId
    ) {
      const card = registeredCards.find(
        (c) => c.paymentMethodId === paymentState.payment!.cardId
      );
      if (card) {
        return {
          source: "saved",
          paymentMethodId: card.paymentMethodId,
          brand: card.brand,
          last4: card.last4,
        };
      }
    }
    if (
      paymentState.payment?.type === paymentType.CARD &&
      fundingInstrument?.source === "new"
    ) {
      return fundingInstrument;
    }
    return null;
  }, [fundingInstrument, paymentState.payment, registeredCards]);

  const [commitHydrated, setCommitHydrated] = useState(
    () => useStore.persist.hasHydrated()
  );
  useEffect(() => {
    if (useStore.persist.hasHydrated()) {
      setCommitHydrated(true);
      return;
    }
    return useStore.persist.onFinishHydration(() => setCommitHydrated(true));
  }, []);

  const claimDefaultAnchor = resolveAmountAnchor(config);
  const seededLinkIdRef = useRef<string | null>(null);
  const payoutSeededLinkRef = useRef<string | null>(null);
  const linkId = session.data?.linkId;

  useEffect(() => {
    if (
      !linkId ||
      (!payoutEditable && !destinationCurrencyEditable)
    ) {
      return;
    }
    if (payoutSeededLinkRef.current === linkId) return;
    payoutSeededLinkRef.current = linkId;
    if (destinationCurrencyEditable && config?.destinationCurrency) {
      setDestinationCurrency(config.destinationCurrency);
    }
    if (destinationCurrencyEditable && config?.sourceCurrency) {
      setSourceCurrency(config.sourceCurrency);
    }
    if (payoutEditable && !useComposeStore.getState().selection) {
      const seeded = claimDefaultPayoutSelection(session.data?.userData);
      if (seeded) setComposeSelection(seeded);
    }
  }, [
    config?.destinationCurrency,
    config?.sourceCurrency,
    destinationCurrencyEditable,
    linkId,
    payoutEditable,
    session.data?.userData,
    setComposeSelection,
    setDestinationCurrency,
    setSourceCurrency,
  ]);
  // Seed the pinned leg from a frozen quote (CIP return) or the suggested claim.
  useEffect(() => {
    if (!commitHydrated || !linkId) return;

    const defaultAnchor = amountEditable
      ? (quoteState?.anchor ?? claimDefaultAnchor)
      : claimDefaultAnchor;

    // Same link already seeded (CIP return or locked config.amount): avoid
    // setState loops — amountState is in the effect deps.
    if (
      seededLinkIdRef.current === linkId &&
      amountState.amount &&
      amountState.anchor
    ) {
      return;
    }

    if (amountState.amount && !amountState.anchor) {
      seededLinkIdRef.current = linkId;
      const padded =
        formatMoney(amountState.amount) || amountState.amount;
      setAmountState({ amount: padded, anchor: defaultAnchor });
      return;
    }

    const frozen =
      amountEditable && quoteState != null
        ? defaultAnchor === "receive"
          ? quoteState.receiveAmount
          : quoteState.sendAmount
        : null;
    const nextRaw =
      !amountEditable &&
      config?.amount != null &&
      config.amount > 0
        ? String(config.amount)
        : typeof frozen === "number" && frozen > 0
          ? String(frozen)
          : config?.amount != null && config.amount > 0
            ? String(config.amount)
            : null;
    const next = nextRaw ? formatMoney(nextRaw) || nextRaw : null;

    seededLinkIdRef.current = linkId;
    if (next) {
      setAmountState({ amount: next, anchor: defaultAnchor });
    }
  }, [
    amountState.amount,
    amountState.anchor,
    amountEditable,
    claimDefaultAnchor,
    commitHydrated,
    config?.amount,
    linkId,
    quoteState,
    setAmountState,
  ]);

  // Drop a persisted quote when the pinned leg or amount no longer matches it.
  useEffect(() => {
    if (!commitHydrated || !quoteState || pricingAmount == null) return;
    const quoteAnchor = quoteState.anchor ?? amountAnchor;
    if (quoteAnchor !== amountAnchor) {
      setQuoteState(null);
      return;
    }
    const frozen =
      quoteAnchor === "receive" ? quoteState.receiveAmount : quoteState.sendAmount;
    if (frozen !== pricingAmount) {
      setQuoteState(null);
    }
  }, [
    amountAnchor,
    commitHydrated,
    pricingAmount,
    quoteState,
    setQuoteState,
  ]);

  // The full-screen alert belongs to the claim's own destination. A payout the
  // payer picked that cannot be priced is a recoverable inline error instead —
  // it must not take over the screen while they are still composing.
  const payoutPickedByPayer =
    payoutEditable && composeSelection?.origin === "user";
  const pickedPayoutUnavailable = routeUnavailable && payoutPickedByPayer;

  useEffect(() => {
    if (routeUnavailable && !payoutPickedByPayer) {
      setShowRouteUnavailable(true);
    }
  }, [routeUnavailable, payoutPickedByPayer]);

  useEffect(() => {
    if (cardClassNoticeHandledRef.current) return;
    if (!cardClassNotice) return;
    if (cardClassNotice.kind === "changed" && availableFundingMethods.length === 0) {
      return;
    }

    cardClassNoticeHandledRef.current = true;
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });

    if (cardClassNotice.kind === "unclassified") {
      setBlockedFundingCard(sheetCardFromNotice(cardClassNotice.card));
      return;
    }

    if (!availableFundingMethods.includes(cardClassNotice.fundingMethod)) {
      setPendingFundingMethod(null);
      setFundingInstrument(null);
      setPaymentState({ payment: null });
      setBlockedFundingCard(sheetCardFromNotice(cardClassNotice.card));
      return;
    }

    setClassChangedToast(cardClassNotice.fundingMethod);
  }, [
    availableFundingMethods,
    cardClassNotice,
    location.pathname,
    location.search,
    navigate,
    setFundingInstrument,
    setPaymentState,
    setPendingFundingMethod,
  ]);

  useEffect(() => {
    // Warm cache is not final. A card registered while this page was
    // unmounted is missing from that snapshot and must not be cleared.
    if (!paymentMethodsLoaded) return;
    if (
      persistedCardIsMissing(
        paymentState.payment,
        fundingInstrument,
        registeredCards
      )
    ) {
      setPendingFundingMethod(null);
      setFundingInstrument(null);
      setPaymentState({ payment: null });
      return;
    }
    const card = resolveFundingCardFromState(
      paymentState.payment,
      fundingInstrument,
      registeredCards
    );
    if (!card) return;
    const method = catalogFundingMethodOf(card.fundingMethod);
    if (!method) {
      setPendingFundingMethod(null);
      setFundingInstrument(null);
      setPaymentState({ payment: null });
      setBlockedFundingCard(card);
      return;
    }
    if (pendingFundingMethod === method) return;
    if (availableFundingMethods.includes(method)) {
      setPendingFundingMethod(method);
      return;
    }
    if (availableFundingMethods.length === 0) return;
    setPendingFundingMethod(null);
    setFundingInstrument(null);
    setPaymentState({ payment: null });
    setBlockedFundingCard(card);
  }, [
    availableFundingMethods,
    fundingInstrument,
    paymentMethodsLoaded,
    paymentState.payment,
    pendingFundingMethod,
    registeredCards,
    setFundingInstrument,
    setPaymentState,
    setPendingFundingMethod,
  ]);

  useEffect(() => {
    if (!pendingFundingMethod || availableFundingMethods.length === 0) return;
    if (
      !availableFundingMethods.includes(
        pendingFundingMethod as (typeof FUNDING_METHODS)[number]
      )
    ) {
      setPendingFundingMethod(null);
      if (paymentState.payment != null) {
        setPaymentState({ payment: null });
      }
      setFundingInstrument(null);
    }
  }, [
    availableFundingMethods,
    pendingFundingMethod,
    paymentState.payment,
    setPaymentState,
    setFundingInstrument,
    setPendingFundingMethod,
  ]);

  const handleFundingPick = useCallback(
    (pick: FlatFundingPick) => {
      if (pick.kind === "add") {
        setPendingFundingMethod(pick.method);
        setFundingInstrument(null);
        safeCapture(getEvents().FUNDING_SELECTED, {
          funding_method: pick.method,
        });
        setPopup(null);
        redirectTo(APP_ROUTES.ADD_CARD_PAGE);
        return;
      }
      if (pick.kind === "card") {
        const method = catalogFundingMethodOf(pick.card.fundingMethod);
        if (!method) {
          setBlockedFundingCard(pick.card);
          setPopup(null);
          return;
        }
        setPendingFundingMethod(method);
        setFundingInstrument({
          source: "saved",
          paymentMethodId: pick.card.paymentMethodId,
          brand: pick.card.brand,
          last4: pick.card.last4,
        });
        setPaymentState({
          payment: {
            cardId: pick.card.paymentMethodId,
            type: paymentType.CARD,
            brand: `${(pick.card.brand || "").toUpperCase()}-${pick.card.last4}`,
          },
        });
        safeCapture(getEvents().FUNDING_SELECTED, { funding_method: method });
        safeCapture(getEvents().CARD_REGISTERED, {
          success: true,
          card_brand: pick.card.brand,
          funding_payment_method_id: pick.card.paymentMethodId,
          last4: pick.card.last4,
        });
        setPopup(null);
        return;
      }
      if (pick.kind === "account") {
        const method =
          availableFundingMethods.find(
            (m) => m === "ACH" || m === "RTP" || m === "BANK_BALANCE"
          ) ?? "ACH";
        setPendingFundingMethod(method);
        setFundingInstrument(null);
        setPaymentState({
          payment: {
            cardId: pick.account.paymentMethodId,
            type: paymentType.BANK,
            brand:
              formatBankAccountLabel(
                pick.account.bankName,
                pick.account.maskedAccountNumber,
              ) ?? pick.account.bankName,
          },
        });
        safeCapture(getEvents().FUNDING_SELECTED, { funding_method: method });
        setPopup(null);
        return;
      }
      const method = pick.kind === "cash" ? "CASH_DEPOSIT" : pick.method;
      setPendingFundingMethod(method);
      setFundingInstrument(null);
      if (method === "CASH_DEPOSIT") {
        setPaymentState({
          payment: {
            cardId: null,
            type: paymentType.CASH,
            brand: t("payment.cashDeposit"),
          },
        });
      } else {
        setPaymentState({
          payment: {
            cardId: null,
            type: paymentType.BANK,
            brand: t("transfer.bankAccount", { last4: "" }).trim(),
          },
        });
      }
      safeCapture(getEvents().FUNDING_SELECTED, { funding_method: method });
      setPopup(null);
    },
    [
      availableFundingMethods,
      redirectTo,
      setFundingInstrument,
      setPaymentState,
      setPendingFundingMethod,
      t,
    ]
  );

  const pricingLoading = preQuoteLoading || quoteLoading;
  const quoteLoadingTimedOut = useQuoteLoadingTimedOut(quoteLoading);
  const initialPreQuoteTimedOut = useQuoteLoadingTimedOut(preQuoteLoading);
  const initialMethodsTimedOut = useQuoteLoadingTimedOut(paymentMethodsLoading);
  const quoteFailed = Boolean(quoteError) || quoteLoadingTimedOut;
  // Settled pre-quote with no SKU for the pick — not a loading state.
  const skuMissing =
    !preQuoteLoading && !!paymentState.payment && !capabilityKey;

  // A selected method is invalid when the binding quote was rejected, or when
  // the chosen method has no quotable SKU. In that case the user must pick
  // another payment method and the confirm button stays disabled.
  const paymentMethodInvalid =
    !!paymentState.payment && (quoteFailed || skuMissing);

  const amountReady = !!pricingAmount;
  // CARD needs a concrete instrument; CASH/ACCOUNT only need the category pick.
  const paymentReady = Boolean(
    paymentState.payment &&
      (paymentState.payment.type !== paymentType.CARD ||
        cvvGate?.funding ||
        resolveFundingInstrument())
  );
  const quoteMatches = quoteMatchesSelection(quote, quoteLoading, {
    fundingMethod: pendingFundingMethod,
    payoutMethod: payoutEditable
      ? (composeSelection?.method ?? payoutMethod ?? null)
      : (payoutMethod ?? null),
    capabilityKey: selectedOption?.capabilityKey ?? capabilityKey,
  });
  const blockForPricing =
    amountReady &&
    paymentReady &&
    !quoteMatches &&
    !quoteFailed &&
    !skuMissing;
  const showQuoteLoading =
    (quoteLoading || blockForPricing) && !quoteFailed && !skuMissing;
  // Rainbow only when ready; button stays clickable so incomplete presses can
  // nudge missing fields (send / request parity).
  const canConfirm =
    amountReady &&
    paymentReady &&
    quoteMatches &&
    !paymentMethodInvalid &&
    !routeUnavailable;
  const showRainbow =
    (canConfirm || confirmLoading) &&
    !fundingLimitsLoading &&
    !amountOutOfRange;

  /**
   * Shake + scroll to incomplete compose gaps. Never opens the funding popup —
   * the user must tap "Pagando con" to choose a method.
   */
  const nudgeIncompleteCompose = useCallback(() => {
    setShake(true);
    window.setTimeout(() => setShake(false), 500);
    const scrollToGap = () => {
      const target =
        document.querySelector(".payout-container-content.payment-required") ??
        document.querySelector(".payout-container-content.shake") ??
        document.querySelector(".estimated-quote-section.shake") ??
        document.querySelector(".estimated-quote-section");
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
    };
    requestAnimationFrame(scrollToGap);
    window.setTimeout(scrollToGap, 50);
  }, []);

  // On confirm we resolve the m2m commit path: freeze the quote, ask the
  // backend whether CIP must run (`cip-readiness`), and either launch the
  // embedded Kentucky SDK (CIP page) or jump straight to creating the
  // operation (sending-money page).
  const handleConfirmClick = useCallback(
    async (cvv?: string) => {
    if (confirmLoading) return;

    // Always allow the press: guide to missing fields — never open funding UI.
    if (!amountReady || !paymentReady || !paymentState.payment) {
      nudgeIncompleteCompose();
      return;
    }
    if (routeUnavailable || paymentMethodInvalid) return;

    if (fundingLimitsLoading) return;
    if (amountOutOfRange) {
      setPopup("amountLimit");
      return;
    }

    // Compose complete — wait for a quote of the selected rail (not a stale JWT).
    if (!quoteMatches || !quote || !capabilityKey) {
      return;
    }

    const payment = paymentState.payment;

    const linkId = session.data?.linkId;
    if (!linkId) {
      console.error("[TransferPage] No linkId found in session");
      return;
    }

    if (payment.type === paymentType.CARD) {
      const cardForGate = resolveFundingCardFromState(
        payment,
        fundingInstrument,
        registeredCards
      );
      if (
        cardForGate &&
        isFundingCardBlocked(cardForGate, availableFundingMethods)
      ) {
        setBlockedFundingCard(cardForGate);
        return;
      }
    }

    setConfirmLoading(true);
    setPrepareError(null);
    safeCapture(getEvents().CONTINUE_CLICK);

    try {
      setQuoteState(quote);

      let preparedFundingId: string | undefined;

      if (payment.type === paymentType.CARD) {
        const instrument = cvvGate?.funding ?? resolveFundingInstrument();
        if (!instrument) {
          nudgeIncompleteCompose();
          return;
        }

        const fundingResult = await prepareFundingRail({
          linkId,
          funding: instrument,
          capabilityKey,
          cvv,
          ...(pendingFundingMethod === "DEBIT_CARD" ||
          pendingFundingMethod === "CREDIT_CARD"
            ? { fundingMethod: pendingFundingMethod }
            : {}),
        });

        setFundingInstrument(fundingResult.funding);
        preparedFundingId = fundingResult.funding.paymentMethodId;
        void refreshPaymentMethods();

        setPaymentState({
          payment: {
            cardId: fundingResult.funding.paymentMethodId,
            type: paymentType.CARD,
            brand: `${(fundingResult.funding.brand || "").toUpperCase()}-${fundingResult.funding.last4}`,
          },
        });

        if (fundingResult.status === "needs_cvv") {
          setCvvGate({
            last4: fundingResult.funding.last4,
            brand: fundingResult.funding.brand,
            funding: fundingResult.funding,
            error:
              fundingResult.errorCode &&
              fundingResult.errorCode !== "CVV_REQUIRED"
                ? prepareErrorMessage(fundingResult.errorCode)
                : null,
          });
          return;
        }

        if (fundingResult.status === "failed") {
          const reason = reasonFromCardPayCode(
            fundingResult.errorCode,
            fundingResult.retryLater,
          );
          safeCapture(getEvents().CARD_REGISTERED, {
            success: false,
            error_code: fundingResult.errorCode ?? "PROVISION_FAILED",
            card_brand: fundingResult.funding.brand,
            funding_payment_method_id: fundingResult.funding.paymentMethodId,
          });
          if (reason) {
            const listed = resolveFundingCardFromState(
              payment,
              fundingResult.funding,
              registeredCards,
            );
            if (
              reason === "railRejected" ||
              reason === "railTemporarilyUnavailable"
            ) {
              const paymentMethodId = fundingResult.funding.paymentMethodId;
              setBlockedRailCards((prev) => {
                const next = new Map(prev);
                next.set(paymentMethodId, reason);
                return next;
              });
              setFundingInstrument(null);
              setPaymentState({ payment: null });
              safeCapture(getEvents().CARD_UNAVAILABLE_SHOWN, { reason });
            }
            setBlockedFundingCard(
              listed ?? {
                paymentMethodId: fundingResult.funding.paymentMethodId,
                last4: fundingResult.funding.last4,
                brand: fundingResult.funding.brand,
                expiryMonth: "",
                expiryYear: "",
                enabled: true,
                isDefault: false,
                fundingMethod: fundingResult.funding.fundingMethod,
              },
            );
            return;
          }
          setPrepareError(prepareErrorMessage(fundingResult.errorCode));
          return;
        }

        safeCapture(getEvents().CARD_REGISTERED, {
          success: true,
          card_brand: fundingResult.funding.brand,
          funding_payment_method_id: fundingResult.funding.paymentMethodId,
          last4: fundingResult.funding.last4,
        });
        setCvvGate(null);
      }

      setPreparedFundingPaymentMethodId(preparedFundingId ?? null);

      if (
        composeSelection &&
        shouldPreparePayoutRail(
          payoutEditable,
          composeSelection.method,
          composeSelection.origin,
        )
      ) {
        if (
          !composeSelection.payout.paymentMethodId &&
          (isPayoutCardValidationStale(
            composeSelection.payout.validatedFor,
            sourceCountry,
            destinationCountry,
          ) ||
            !composeSelection.payout.cvsValidationId)
        ) {
          setPrepareError(prepareErrorMessage("CARD_PUSH_STALE"));
          return;
        }
        const cardholderName = session.data?.userData?.name
          ? [
              session.data.userData.name.firstName,
              session.data.userData.name.lastName,
            ]
              .filter(Boolean)
              .join(" ")
          : session.data?.userData?.fullName;
        const payoutResult = await preparePayoutRail({
          linkId,
          payout: composeSelection.payout,
          capabilityKey,
          cardholderName,
        });
        if (payoutResult.status === "failed") {
          setPrepareError(prepareErrorMessage(payoutResult.errorCode));
          return;
        }
        setPreparedPayoutPaymentMethodId(
          payoutResult.payout.paymentMethodId ?? null,
        );
      } else {
        setPreparedPayoutPaymentMethodId(null);
      }

      const cipResult = await checkCipReadiness(linkId);

      if (cipResult.shouldRunCip && cipResult.cipTicketId) {
        redirectTo(APP_ROUTES.CIP_PAGE, {
          replace: true,
          state: { cipTicketId: cipResult.cipTicketId },
        });
        return;
      }

      const prompt = shouldPromptPhone(session.data, linkId);
      if (prompt) {
        redirectTo(APP_ROUTES.PHONE_PAGE, {
          replace: true,
          state: { next: APP_ROUTES.SENDING_MONEY_PAGE },
        });
        return;
      }
      await ensureSmsConsent(linkId, i18n.language?.slice(0, 2) || "en");
      redirectTo(APP_ROUTES.SENDING_MONEY_PAGE, {
        replace: true,
        state: { next: APP_ROUTES.SENDING_MONEY_PAGE },
      });
    } catch (err) {
      // Link expired / revoked: the dead-link handler already routed to the
      // screen that explains why the link stopped working.
      if (isDeadLinkError(err)) {
        return;
      }

      const code =
        err && typeof err === "object" && "code" in err
          ? String((err as { code?: string }).code)
          : undefined;
      if (code === "REGISTER_FAILED" || code === "VGS_CONFIG_UNAVAILABLE") {
        setPrepareError(prepareErrorMessage(code));
        return;
      }

      console.error("[TransferPage] Confirm failed:", err);
      navigate(APP_ROUTES.ERROR_PAGE, {
        state: {
          title: t("transfer.error"),
          subTitle: null,
          message:
            err instanceof Error ? err.message : t("error.tryAgainLater"),
          btnText: t("common.retry"),
          isOut: false,
          pageUrl: APP_ROUTES.TRANSFER_PAGE,
        },
      });
    } finally {
      setConfirmLoading(false);
    }
  },
    [
    confirmLoading,
    amountReady,
    paymentReady,
    paymentState.payment,
    paymentMethodInvalid,
    routeUnavailable,
    quoteMatches,
    quote,
    capabilityKey,
    fundingLimitsLoading,
    amountOutOfRange,
    session.data?.linkId,
    setQuoteState,
    setPreparedFundingPaymentMethodId,
    setFundingInstrument,
    setPaymentState,
    resolveFundingInstrument,
    cvvGate,
    redirectTo,
    navigate,
    prepareErrorMessage,
    nudgeIncompleteCompose,
    refreshPaymentMethods,
    t,
    fundingInstrument,
    registeredCards,
    availableFundingMethods,
    pendingFundingMethod,
  ]);

  const exchangeRate = economics?.exchangeRate ?? 0;
  const fee = quoteMatches
    ? (quote?.totalFee ?? quote?.fee ?? null)
    : (selectedOption?.customerFee ??
      (economics?.estimated ? economics.fee : null) ??
      null);

  const firstSend = useFirstSendWarning({
    linkId,
    sendAmount:
      quote?.sendAmount ??
      selectedOption?.sendAmount ??
      bestOption?.sendAmount ??
      economics?.sendAmount ??
      null,
    sourceCurrency,
    fundingMethod: pendingFundingMethod,
  });

  const sendFormatted = useMemo(() => {
    const value =
      quote?.sendAmount ??
      selectedOption?.sendAmount ??
      bestOption?.sendAmount ??
      economics?.sendAmount;
    if (value == null) return undefined;
    return amountFormatter(value, sourceCurrency, { country: "en-US" });
  }, [
    quote?.sendAmount,
    selectedOption?.sendAmount,
    bestOption?.sendAmount,
    economics?.sendAmount,
    sourceCurrency,
  ]);

  const receiveFormatted = useMemo(() => {
    const value =
      quote?.receiveAmount ??
      selectedOption?.receiveAmount ??
      bestOption?.receiveAmount ??
      economics?.receiveAmount;
    if (value == null) return undefined;
    return amountFormatter(value, destinationCurrency, { country: "en-US" });
  }, [
    quote?.receiveAmount,
    selectedOption?.receiveAmount,
    bestOption?.receiveAmount,
    economics?.receiveAmount,
    destinationCurrency,
  ]);

  const totalPayment = useMemo(() => {
    if (economics?.totalCost != null) {
      return formatLabeledAmount(economics.totalCost, sourceCurrency);
    }
    if (amountAnchor === "send" && amountState.amount && fee != null) {
      const parsed = parseFloat(amountState.amount.replace(/,/g, ""));
      if (Number.isFinite(parsed)) {
        return formatLabeledAmount(parsed + fee, sourceCurrency);
      }
    }
    return null;
  }, [economics, amountAnchor, amountState.amount, fee, sourceCurrency]);

  const feeDisplay = useMemo(
    () =>
      fee != null
        ? formatLabeledAmount(fee, sourceCurrency)
        : formatLabeledAmount(0, sourceCurrency),
    [fee, sourceCurrency]
  );

  const rateText = useMemo(() => {
    if (pricingLoading) return "...";
    return `1 ${sourceCurrency} = ${exchangeRate.toFixed(4)} ${destinationCurrency}`;
  }, [pricingLoading, sourceCurrency, exchangeRate, destinationCurrency]);

  const openFxDisclaimer = useCallback(() => setShowExcratePopup(true), []);
  const onFxDisclaimerKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        setShowExcratePopup(true);
      }
    },
    []
  );

  const pickIcon = useCallback((type: paymentType, brand: string) => {
    switch (type) {
      case paymentType.CARD: {
        let finalIcon = null;
        if (brand.toLocaleUpperCase().includes("VISA")) finalIcon = visaLogo;
        if (brand.toLocaleUpperCase().includes("MASTERCARD"))
          finalIcon = masterCardLogo;

        return {
          brand,
          icon: <img src={finalIcon ?? ""} alt={`${brand} logo`} />,
        };
      }
      case paymentType.BANK:
        return {
          brand,
          icon: <BankIcon size={20} />,
        };
      case paymentType.CASH:
        return {
          brand,
          icon: <CashSpecialIcon size={20} />,
        };

      default:
        return {
          brand,
          icon: <WalletIcon size={20} />,
        };
    }
  }, []);

  const paymentSelected = useMemo(
    () =>
      paymentState.payment
        ? pickIcon(paymentState.payment.type, paymentState.payment.brand)
        : {
            brand: t("transfer.selectPaymentMethod"),
            icon: <WalletIcon size={20} />,
          },
    [paymentState, pickIcon, t]
  );

  // Destination payout, fixed by the claim ticket (`userData.paymentMethod`).
  // Each payout type renders its own label + icon.
  const recipientFirstName = useMemo(() => {
    const userData = session.data?.userData;
    return (
      userData?.name?.firstName?.trim() ||
      userData?.fullName?.trim().split(/\s+/).filter(Boolean)[0] ||
      t("fundRequest.someone")
    );
  }, [session.data?.userData, t]);

  const payoutDestination = useMemo(() => {
    const pm = session.data?.userData?.paymentMethod;
    const last4 = (value?: string) => (value ? value.slice(-4) : "");

    // With `allowPayoutEdit` the row tracks compose, not the claim snapshot.
    if (payoutEditable && composeSelection) {
      const { method, payout } = composeSelection;
      switch (method) {
        case PayoutMethodType.CASH_PICKUP:
          return {
            subtitle: payerName
              ? t("transfer.cashPickup", { payer: payerName })
              : t("transfer.payoutAccount"),
            icon: <CashSpecialIcon size={20} />,
          };
        case PayoutMethodType.PUSH_TO_CARD:
          return {
            subtitle: payout.last4
              ? t("transfer.cardEnding", { last4: payout.last4 })
              : t("transfer.payoutAccount"),
            icon: <WalletIcon size={20} />,
          };
        case PayoutMethodType.BANK_ACCOUNT: {
          const stored =
            composeSelection.origin === "claim"
              ? pm?.bankAccount?.bankName
              : payout.bankId;
          const catalog =
            composeSelection.origin === "claim"
              ? payerName
              : selectedBankName || payerName;
          return {
            subtitle: bankAccountRowLabel({
              stored,
              catalogName: catalog,
              accountOrLast4: last4(payout.accountNumber) || payout.last4,
              unnamedWithDigits: t("transfer.bankAccount", {
                last4: last4(payout.accountNumber) || payout.last4,
              }),
              empty: t("transfer.payoutAccount"),
            }),
            icon: <BankIcon size={20} />,
          };
        }
        case PayoutMethodType.MOBILE_WALLET:
          return {
            subtitle: payout.phone || t("transfer.payoutAccount"),
            icon: <WalletIcon size={20} />,
          };
        default:
          return {
            subtitle: t("transfer.payoutAccount"),
            icon: <BankIcon size={20} />,
          };
      }
    }

    switch (pm?.type) {
      case "cash": {
        return {
          subtitle: payerName
            ? t("transfer.cashPickup", { payer: payerName })
            : t("transfer.payoutAccount"),
          icon: <CashSpecialIcon size={20} />,
        };
      }
      case "card": {
        const digits = last4(pm.card?.last4);
        return {
          subtitle: digits
            ? t("transfer.cardEnding", { last4: digits })
            : t("transfer.payoutAccount"),
          icon: <WalletIcon size={20} />,
        };
      }
      case "bank_account": {
        return {
          subtitle: bankAccountRowLabel({
            stored: pm.bankAccount?.bankName,
            catalogName: payerName,
            accountOrLast4: pm.bankAccount?.accountNumber,
            unnamedWithDigits: t("transfer.bankAccount", {
              last4: last4(pm.bankAccount?.accountNumber),
            }),
            empty:
              pm.bankAccount?.bankName ||
              payerName ||
              t("transfer.payoutAccount"),
          }),
          icon: <BankIcon size={20} />,
        };
      }
      default:
        return {
          subtitle: t("transfer.payoutAccount"),
          icon: <BankIcon size={20} />,
        };
    }
  }, [
    session.data?.userData?.paymentMethod,
    payoutEditable,
    composeSelection,
    payerName,
    selectedBankName,
    t,
  ]);

  const { openSupport, supportSheet } = useSupportSheet();

  const claimHasAmount =
    (config?.amount != null && config.amount > 0) ||
    Boolean(amountState.amount) ||
    (amountEditable &&
      quoteState != null &&
      ((quoteState.anchor === "receive"
        ? quoteState.receiveAmount
        : quoteState.sendAmount) ?? 0) > 0);
  const initialPaintLatched = useRef(false);
  const initialPaintReady = isInitialTransferPaintReady({
    sessionReady,
    amountResolved:
      sessionReady && commitHydrated && (!claimHasAmount || pricingAmount != null),
    needsPrice: pricingAmount != null,
    pricingLoading: preQuoteLoading && !initialPreQuoteTimedOut,
    pricingSettled:
      Boolean(preQuoteError) ||
      routeUnavailable ||
      initialPreQuoteTimedOut ||
      (economics?.sendAmount != null && economics.receiveAmount != null),
    methodsLoading: paymentMethodsLoading && !initialMethodsTimedOut,
  });
  if (initialPaintReady) initialPaintLatched.current = true;

  if (!sessionReady || !initialPaintLatched.current) {
    return (
      <AuraLayout colorConfig={colorConfig}>
        <Layout.Content>
          <div className="landing-section content-center loader-page">
            <section className="content">
              <div
                className="loader-spinner"
                role="status"
                aria-label={t("transfer.loadingQuote", "Loading...")}
              />
            </section>
          </div>
        </Layout.Content>
      </AuraLayout>
    );
  }

  if (showRouteUnavailable || (routeUnavailable && !payoutPickedByPayer)) {
    return (
      <RouteUnavailablePage
        destinationCountry={destinationCountry}
        payerCode={payerCode}
        payerName={payerName}
        onClose={() => setShowRouteUnavailable(false)}
      />
    );
  }

  return (
    <>
      <AuraLayout colorConfig={colorConfig} className="scrollable need-help-header">
        <Layout.Header>
          <Navbar
            title={t("common.needHelp")}
            align="right"
            onBackClick={() =>
              redirectTo(APP_ROUTES.FUND_REQUEST_PAGE, { replace: true })
            }
            onIconClick={() => openSupport()}
            icon={<SupportIcon size={35} colors={dropzoneColors} />}
          />
        </Layout.Header>
        <Layout.Content className="transfer-section">
          <ScrollFadeIndicator color={bodyCopy} />
          <Title
            title={t("transfer.requestedBy", { name: recipientFirstName })}
            align="left"
            color={TITLE_COLOR}
          />
          <section
            className={`estimated-quote-section stacked-quote${
              shake && !amountReady ? " shake" : ""
            }`}
          >
            <section className="quote-container">
              <div className="banrural-amounts">
                <div className="banrural-send-wrap">
                  <QuoteInfo
                    label={t("transfer.send")}
                    currency={sourceCurrency}
                    textColor={bodyCopy}
                    mode="input"
                    side="send"
                    amountPrefix={sourceAmountPrefix}
                    isActive={amountAnchor === "send"}
                    readOnly={!amountEditable}
                    derivedAmount={
                      amountAnchor === "receive" ? sendFormatted : undefined
                    }
                    showSelectorChevron={destinationCurrencyEditable}
                    onSelect={
                      destinationCurrencyEditable
                        ? () => setCurrencyPopup("source")
                        : undefined
                    }
                    icon={<FlagCircle iso2={sourceCountry.toLowerCase()} />}
                  />
                  <span
                    className="banrural-swap"
                    role="button"
                    tabIndex={0}
                    onClick={openFxDisclaimer}
                    onKeyDown={onFxDisclaimerKeyDown}
                    style={{ background: backgroundBtn }}
                  >
                    <ExchangeIcon size={18} colors={[textColorBtn, textColorBtn]} />
                  </span>
                </div>
                <QuoteInfo
                  label={t("transfer.receive")}
                  currency={destinationCurrency}
                  textColor={bodyCopy}
                  mode="input"
                  side="receive"
                  amountPrefix={destinationAmountPrefix}
                  isActive={amountAnchor === "receive"}
                  readOnly={!amountEditable}
                  derivedAmount={
                    amountAnchor === "send" ? receiveFormatted : undefined
                  }
                  showSelectorChevron={destinationCurrencyEditable}
                  onSelect={
                    destinationCurrencyEditable
                      ? () => setCurrencyPopup("destination")
                      : undefined
                  }
                  icon={<FlagCircle iso2={destinationCountry.toLowerCase()} />}
                  bottomSlot={
                    <section
                      className="banrural-exchange-rate banrural-exchange-rate--alert"
                      role="button"
                      tabIndex={0}
                      onClick={openFxDisclaimer}
                      onKeyDown={onFxDisclaimerKeyDown}
                      style={
                        {
                          "--bgc": bannerHighlightBackground ?? bodyCopy,
                          "--colorc": solidStop(
                            bannerHighlightIconColors?.[0] ?? title,
                          ),
                          "--fillc": bannerHighlightColor ?? title,
                        } as CSSProperties
                      }
                    >
                      <span className="banrural-exchange-rate-icon">i</span>
                      <span className="banrural-exchange-rate-text">
                        <b>{rateText}</b>
                        <label>{t("exchange.estimatedRate")}</label>
                      </span>
                    </section>
                  }
                />
              </div>
            </section>
          </section>
          <PayoutInfo
            title={t("transfer.payingWith")}
            subtitle={paymentSelected.brand}
            icon={paymentSelected.icon}
            background={backgroundBtn}
            color={textColorBtn}
            onChange={() => {
              void refreshPaymentMethods();
              setPopup("funding");
            }}
            className={`${!paymentReady ? "payment-required" : ""} ${
              shake && !paymentReady ? "shake" : ""
            }`.trim()}
          />
          <PayoutInfo
            title={t("transfer.receiveInForRecipient", {
              name: recipientFirstName,
            })}
            subtitle={payoutDestination.subtitle}
            icon={payoutDestination.icon}
            background={payoutEditable ? backgroundBtn : "#f7f7f7"}
            color={payoutEditable ? textColorBtn : TITLE_COLOR}
            locked={!payoutEditable}
            onChange={
              payoutEditable ? () => setPopup("payout") : undefined
            }
          />
          {pickedPayoutUnavailable ? (
            <BodyCopy className="payment-error-text" role="alert">
              {t("transfer.payoutUnavailable")}
            </BodyCopy>
          ) : null}
          {prepareError ? (
            <BodyCopy className="payment-error-text" role="alert">
              {prepareError}
            </BodyCopy>
          ) : null}
          {skuMissing ? (
            <BodyCopy className="payment-error-text" role="alert">
              {t("transfer.paymentMethodUnavailable")}
            </BodyCopy>
          ) : null}
          {quoteFailed && !skuMissing ? (
            <>
              <BodyCopy className="payment-error-text" role="alert">
                {t("transfer.quoteError")}
              </BodyCopy>
              <Button
                variant="cancel"
                size="small"
                color={bodyCopy}
                onClick={() => {
                  void refetchQuote();
                }}
              >
                {t("transfer.quoteRetry")}
              </Button>
            </>
          ) : null}
          {firstSend.visible ? (
            <FirstSendWarning
              className="first-send-warning--spaced"
              title={firstSend.title}
              body={firstSend.body}
            />
          ) : null}
          <BodyCopy
            className="footer-text transfer-timing-note"
            textColor={bodyCopy}
          >
            <b>{t("transfer.instantTransfer")}</b>
            {" "}
            <span className="transfer-timing-note-rest">
              {t("transfer.mayTakeFew")}
            </span>
          </BodyCopy>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section transfer-footer">
            <FooterDetailSummary
              heading={t("transfer.detail")}
              fee={t("transfer.fee", {
                amount: feeDisplay,
              })}
              total={t("transfer.totalPayment", {
                amount:
                  totalPayment ?? formatLabeledAmount(0, sourceCurrency),
              })}
              titleColor={TITLE_COLOR}
              textColor={TITLE_COLOR}
            />
            <div className={`button-wrapper ${showRainbow ? "rainbow-ready" : ""}`}>
              <Button
                size="large"
                background={backgroundBtn}
                color={textColorBtn}
                disabled={confirmLoading || blockForPricing || quoteFailed}
                onClick={() => {
                  void handleConfirmClick();
                }}
              >
                {confirmLoading
                  ? t("transfer.prepare.loading")
                  : showQuoteLoading
                    ? t("transfer.loadingQuote", "Loading...")
                    : t("transfer.confirmTransfer")}
              </Button>
            </div>
          </section>
        </Layout.Footer>
      </AuraLayout>
      <Popup
        visible={popup === "amountLimit" && amountOutOfRange}
        onClose={() => setPopup(null)}
      >
        <AmountOutOfRangeInfo
          kind={amountBoundVerdict === "below_min" ? "below_min" : "above_max"}
          method={t(`funding.methods.${pendingFundingMethod ?? ""}`, {
            defaultValue: pendingFundingMethod ?? "",
          })}
          bound={
            amountBoundVerdict === "below_min"
              ? (selectedFundingDetails?.minAmount ?? 0)
              : (selectedFundingDetails?.maxAmount ?? 0)
          }
          currency={sourceCurrency}
          cta={amountEditable ? "changeAmount" : "changeMethod"}
          onClose={() => setPopup(null)}
        />
      </Popup>
      <FundRequestPayoutPopups
        visible={popup === "payout" && payoutEditable}
        onClose={() => setPopup(null)}
        destinationCountry={destinationCountry}
        destinationCurrency={destinationCurrency}
        sourceCountry={sourceCountry}
        fundingMethod={pendingFundingMethod}
        options={payoutDiscoveryOptions}
        optionsLoading={payoutDiscoveryLoading}
        availableMethods={allPayoutMethodsForPicker}
        userData={session.data?.userData}
        onSelectionChange={() => setPayoutRevision((n) => n + 1)}
      />
      {blockedFundingCard ? (
        <Popup
          visible
          onClose={() => setBlockedFundingCard(null)}
        >
          <UnavailableFundingCardSheet
            card={blockedFundingCard}
            available={availableFundingMethods}
            bodyCopy={bodyCopy}
            backgroundBtn={backgroundBtn}
            textColorBtn={textColorBtn}
            onDismiss={() => setBlockedFundingCard(null)}
            showHeaderClose={false}
            reason={
              blockedFundingCard
                ? blockedRailCards.get(blockedFundingCard.paymentMethodId)
                : undefined
            }
            onChangeMethod={
              blockedFundingCard &&
              blockedRailCards.has(blockedFundingCard.paymentMethodId)
                ? () => {
                    setBlockedFundingCard(null);
                    setPopup("funding");
                  }
                : undefined
            }
          />
        </Popup>
      ) : null}
      <Popup visible={popup === "funding"} onClose={() => setPopup(null)}>
        <SelectFundingMethodSlot
          selectedMethod={pendingFundingMethod}
          selectedPaymentMethodId={
            (fundingInstrument?.source === "saved"
              ? fundingInstrument.paymentMethodId
              : null) ||
            paymentState.payment?.cardId ||
            null
          }
          available={availableFundingMethods}
          cards={registeredCards}
          bankAccounts={bankAccounts}
          loading={fundingPickerLoading}
          details={fundingDetails}
          hasQuote={quoteMatches}
          quoteFee={
            quoteMatches ? (quote?.totalFee ?? quote?.fee ?? 0) : null
          }
          composedOptionFee={
            isFundingMethod(pendingFundingMethod) && selectedOption
              ? selectedOption.customerFee
              : null
          }
          currency={sourceCurrency}
          onPick={handleFundingPick}
          onClose={() => setPopup(null)}
          blockedRailCards={blockedRailCards}
        />
      </Popup>
      <Popup visible={currencyPopup != null} onClose={() => setCurrencyPopup(null)}>
        <SelectDestinationCurrencySlot
          title={t(
            currencyPopup === "source"
              ? "transfer.selectSourceCurrency"
              : "transfer.selectDestinationCurrency",
          )}
          options={
            currencyPopup === "source"
              ? sourceCurrencyOptions
              : destinationCurrencyOptions
          }
          selectedCurrency={
            currencyPopup === "source" ? sourceCurrency : destinationCurrency
          }
          onSelect={
            currencyPopup === "source"
              ? handleSourceCurrencySelect
              : handleDestinationCurrencySelect
          }
          onClose={() => setCurrencyPopup(null)}
        />
      </Popup>
      {cvvGate ? (
        <Popup
          visible
          onClose={() => {
            setCvvGate(null);
            setPrepareError(null);
          }}
        >
          <CvvPrepareGate
            last4={cvvGate.last4}
            brand={cvvGate.brand}
            loading={confirmLoading}
            error={cvvGate.error}
            onClose={() => {
              setCvvGate(null);
              setPrepareError(null);
            }}
            onSubmit={(enteredCvv) => {
              void handleConfirmClick(enteredCvv);
            }}
          />
        </Popup>
      ) : null}

      <Popup
        visible={showExcratePopup}
        onClose={() => setShowExcratePopup(false)}
      >
        <section className="qoute-popup">
          <WarningIcon size={60} />
          <BodyCopy textColor={bodyCopy}>
            <Trans i18nKey="transfer.quote.disclaimer" />
          </BodyCopy>
        </section>
      </Popup>
      {classChangedToast ? (
        <Toast
          type="info"
          duration={4000}
          message={t(
            classChangedToast === "CREDIT_CARD"
              ? "payment.classChangedCredit"
              : "payment.classChangedDebit"
          )}
          onClose={() => setClassChangedToast(null)}
        />
      ) : null}
      {supportSheet}
    </>
  );
});
